<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Elder Profiles</h1>
        <p class="text-gray-500 mt-1">Manage and monitor your elderly loved ones</p>
      </div>
      <button
        @click="showCreateModal = true"
        class="btn btn-primary flex items-center space-x-2"
      >
        <PlusIcon class="w-5 h-5" />
        <span>Add New Profile</span>
      </button>
    </div>
    
    <!-- Filters -->
    <div class="card">
      <div class="card-body">
        <div class="flex flex-wrap gap-4">
          <div class="flex-1 min-w-[200px]">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search profiles..."
              class="input w-full"
            />
          </div>
          <select v-model="filterStatus" class="input">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="inactive">Inactive</option>
          </select>
          <select v-model="sortBy" class="input">
            <option value="name">Sort by Name</option>
            <option value="wellness">Sort by Wellness</option>
            <option value="lastCall">Sort by Last Call</option>
          </select>
        </div>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner"></div>
    </div>
    
    <!-- Profiles Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="profile in filteredProfiles"
        :key="profile.id"
        class="card hover:shadow-lg transition-shadow cursor-pointer"
        @click="goToProfile(profile.id)"
      >
        <div class="card-body">
          <!-- Profile Header -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
              <div class="text-4xl mr-3">{{ profile.avatar || 'Y' }}</div>
              <div>
                <h3 class="font-semibold text-lg">{{ profile.name }}</h3>
                <p class="text-sm text-gray-500">{{ profile.relationship }}</p>
              </div>
            </div>
            <div :class="['badge', getStatusBadgeClass(profile.status)]">
              {{ profile.status }}
            </div>
          </div>
          
          <!-- Profile Info -->
          <div class="space-y-2 text-sm">
            <div class="flex items-center text-gray-600">
              <PhoneIcon class="w-4 h-4 mr-2" />
              {{ profile.phone }}
            </div>
            <div class="flex items-center text-gray-600">
              <ClockIcon class="w-4 h-4 mr-2" />
              Last call: {{ formatLastCall(profile.last_call_at) }}
            </div>
            <div class="flex items-center text-gray-600">
              <CalendarIcon class="w-4 h-4 mr-2" />
              Next: {{ profile.preferred_call_time || 'Not scheduled' }}
            </div>
          </div>
          
          <!-- Wellness Score -->
          <div class="mt-4">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs text-gray-500">Wellness Score</span>
              <span class="text-sm font-semibold">{{ getWellnessScore(profile) }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                :class="['h-2 rounded-full transition-all', getWellnessColor(getWellnessScore(profile))]"
                :style="`width: ${getWellnessScore(profile)}%`"
              ></div>
            </div>
          </div>
          
          <!-- Quick Actions -->
          <div class="flex space-x-2 mt-4">
            <button
              @click.stop="initiateCall(profile)"
              class="flex-1 btn btn-primary btn-sm"
            >
              <PhoneIcon class="w-4 h-4 mr-1" />
              Call
            </button>
            <button
              @click.stop="editProfile(profile)"
              class="flex-1 btn btn-secondary btn-sm"
            >
              <PencilIcon class="w-4 h-4 mr-1" />
              Edit
            </button>
            <button
              @click.stop="toggleStatus(profile)"
              class="btn btn-secondary btn-sm"
            >
              <Component :is="profile.status === 'active' ? PauseIcon : PlayIcon" class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      <!-- Add New Profile Card -->
      <div
        @click="showCreateModal = true"
        class="card border-2 border-dashed border-gray-300 hover:border-primary-500 cursor-pointer group"
      >
        <div class="card-body flex flex-col items-center justify-center py-12">
          <div class="w-16 h-16 bg-gray-100 group-hover:bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <PlusIcon class="w-8 h-8 text-gray-400 group-hover:text-primary-600" />
          </div>
          <h3 class="font-semibold text-gray-700 group-hover:text-primary-600">Add New Profile</h3>
          <p class="text-sm text-gray-500 mt-1">Start monitoring a new elder</p>
        </div>
      </div>
    </div>
    
    <!-- Create/Edit Profile Modal -->
    <ProfileModal
      v-if="showCreateModal || editingProfile"
      :profile="editingProfile"
      @save="handleSaveProfile"
      @close="closeModal"
    />
    
    <!-- Call Modal -->
    <QuickCallModal
      v-if="callingProfile"
      :profile="callingProfile"
      @close="callingProfile = null"
      @call="handleCallProfile"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useProfileStore } from '@/stores/profiles'
import { useCallStore } from '@/stores/calls'
import {
  PlusIcon,
  PhoneIcon,
  ClockIcon,
  CalendarIcon,
  PencilIcon,
  PauseIcon,
  PlayIcon
} from '@heroicons/vue/24/outline'
import ProfileModal from '@/components/profiles/ProfileModal.vue'
import QuickCallModal from '@/components/profiles/QuickCallModal.vue'
import dayjs from 'dayjs'

const router = useRouter()
const profileStore = useProfileStore()
const callStore = useCallStore()

const searchQuery = ref('')
const filterStatus = ref('')
const sortBy = ref('name')
const showCreateModal = ref(false)
const editingProfile = ref(null)
const callingProfile = ref(null)

const loading = computed(() => profileStore.loading)
const profiles = computed(() => profileStore.profiles)

const filteredProfiles = computed(() => {
  let result = [...profiles.value]
  
  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(p => 
      p.name.toLowerCase().includes(query) ||
      p.phone.includes(query) ||
      p.relationship?.toLowerCase().includes(query)
    )
  }
  
  // Status filter
  if (filterStatus.value) {
    result = result.filter(p => p.status === filterStatus.value)
  }
  
  // Sorting
  result.sort((a, b) => {
    switch (sortBy.value) {
      case 'wellness':
        return getWellnessScore(b) - getWellnessScore(a)
      case 'lastCall':
        return new Date(b.last_call_at || 0) - new Date(a.last_call_at || 0)
      default:
        return a.name.localeCompare(b.name)
    }
  })
  
  return result
})

function getStatusBadgeClass(status) {
  const classes = {
    active: 'badge-success',
    paused: 'badge-warning',
    inactive: 'badge-danger'
  }
  return classes[status] || 'badge-info'
}

function formatLastCall(date) {
  if (!date) return 'Never'
  return dayjs(date).fromNow()
}

function getWellnessScore(profile) {
  // Calculate from latest call data or return default
  return profile.wellness_score || 75
}

function getWellnessColor(score) {
  if (score >= 80) return 'bg-success-500'
  if (score >= 60) return 'bg-warning-500'
  return 'bg-danger-500'
}

function goToProfile(id) {
  router.push(`/profiles/${id}`)
}

function editProfile(profile) {
  editingProfile.value = profile
}

function initiateCall(profile) {
  callingProfile.value = profile
}

async function toggleStatus(profile) {
  if (profile.status === 'active') {
    await profileStore.pauseProfile(profile.id)
  } else {
    await profileStore.activateProfile(profile.id)
  }
}

async function handleSaveProfile(profileData) {
  if (editingProfile.value) {
    await profileStore.updateProfile(editingProfile.value.id, profileData)
  } else {
    await profileStore.createProfile(profileData)
  }
  closeModal()
}

function closeModal() {
  showCreateModal.value = false
  editingProfile.value = null
}

async function handleCallProfile(profile) {
  try {
    await callStore.initiateCall(profile.id)
    callingProfile.value = null
  } catch (error) {
    console.error('Failed to initiate call:', error)
  }
}

onMounted(() => {
  profileStore.fetchProfiles()
})
</script>

